import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Wrench, Sparkles, ShieldCheck, ShoppingBag, Quote as QuoteIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteCopy } from "@/lib/copy";

const DEFAULT_SERVICES = [
  { key: "pool", title: "Pool Installation", text: "Custom in-ground & above-ground builds engineered for your yard, your light, your life." },
  { key: "repair", title: "Repair & Renovation", text: "Heaters, pumps, liners, plaster — restored to silence and shimmer." },
  { key: "clean", title: "Cleaning & Service", text: "Weekly visits that keep your water glass-clear and chemistry on point." },
  { key: "openings", title: "Openings & Closings", text: "Seasonal openings, winterizations, and safety covers — done right." },
];

const ICONS = { pool: Droplets, repair: Wrench, clean: Sparkles, upgrades: Droplets, openings: ShieldCheck };

export default function Home() {
  const t = useSiteCopy();
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [sectionTitle, setSectionTitle] = useState("What we do, beautifully");
  const [sectionSubtitle, setSectionSubtitle] = useState("Four pillars, one obsession: water that looks effortless.");

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const data = r.data || {};
      if (Array.isArray(data.home_services) && data.home_services.length) setServices(data.home_services);
      if (data.home_section_title) setSectionTitle(data.home_section_title);
      if (data.home_section_subtitle) setSectionSubtitle(data.home_section_subtitle);
    }).catch(() => { /* keep defaults */ });
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="caustic-bg absolute inset-0 -z-10" />
        <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-16 pt-12 md:grid-cols-12 md:px-8 md:pt-20">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-cyan-700 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" /> {t("home.hero_eyebrow", "Veteran Owned & Operated")}
            </div>
            <h1 className="mt-5 font-display text-4xl font-light leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
              {t("home.hero_title_top", "Pools that feel like")}
              <span className="block font-bold shimmer-text">{t("home.hero_title_accent", "a calm exhale.")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
              {t("home.hero_body", "Fix It Aquatics, LLC builds, restores and looks after the most peaceful corner of your home. Schedule a free quote, browse our shop, and let the water do the rest.")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/quote" className="btn-aqua" data-testid="hero-get-quote-button">
                {t("home.hero_cta_primary", "Get a free quote")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/shop" className="btn-ghost-aqua" data-testid="hero-shop-button">
                <ShoppingBag className="h-4 w-4" /> {t("home.hero_cta_secondary", "Visit the shop")}
              </Link>
            </div>
            <dl className="mt-12 grid max-w-lg grid-cols-3 gap-6">
              {[
                [t("home.stat1_value", "20+"), t("home.stat1_label", "years on the water")],
                [t("home.stat2_value", "1.4k"), t("home.stat2_label", "happy backyards")],
                [t("home.stat3_value", "48hr"), t("home.stat3_label", "service response")],
              ].map(([k, v]) => (
                <div key={v}>
                  <dt className="font-display text-3xl font-bold text-slate-900">{k}</dt>
                  <dd className="text-xs uppercase tracking-widest text-slate-500">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative md:col-span-5">
            <div className="floaty">
              <div className="relative overflow-hidden rounded-[2rem] shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1635111300299-e6e677daf7b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Luxury pool"
                  className="h-[440px] w-full object-cover md:h-[520px]"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-cyan-900/60 to-transparent" />
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rotate-[-3deg] sm:block">
              <div className="glass rounded-2xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-yellow-300/80">
                    <Sparkles className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <div className="font-display text-sm font-bold text-slate-900">{t("home.badge_title", "Crystal-clear, certified")}</div>
                    <div className="text-xs text-slate-600">{t("home.badge_subtitle", "Licensed CPO® technicians")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — Bento */}
      <section className="section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="flex items-end justify-between gap-6">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 wavy">{sectionTitle}</h2>
              <p className="mt-2 max-w-xl text-slate-600">{sectionSubtitle}</p>
            </div>
            <Link to="/services" className="hidden text-sm font-medium text-cyan-700 hover:underline md:inline-flex" data-testid="services-see-all-link">
              See all services →
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 md:auto-rows-[220px] md:grid-cols-3">
            {services.map((s, i) => {
              const Icon = ICONS[s.key] || Droplets;
              const big = i === 0;
              return (
                <div
                  key={s.key || s.title}
                  className={`group relative flex min-h-[200px] flex-col overflow-hidden rounded-3xl border border-cyan-100 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${big ? "md:col-span-2 md:row-span-2" : ""}`}
                  data-testid={`service-card-${i}`}
                >
                  <div className={`absolute -right-12 -top-12 h-44 w-44 rounded-full ${big ? "bg-cyan-100" : "bg-cyan-50"} transition-transform duration-500 group-hover:scale-110`} />
                  <Icon className="relative h-7 w-7 text-cyan-600" />
                  <h3 className={`relative mt-3 font-display font-bold text-slate-900 ${big ? "text-2xl" : "text-lg"}`}>{s.title}</h3>
                  <p className="relative mt-2 max-w-sm text-sm text-slate-600">{s.text}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-6 py-3 text-sm font-medium text-cyan-700 shadow-sm transition hover:-translate-y-0.5 hover:border-cyan-400 hover:text-cyan-800 hover:shadow-md"
              data-testid="services-learn-more-link"
            >
              Learn more <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUOTE CTA */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] water-gradient p-10 text-white md:p-14">
            <div className="caustic-bg absolute inset-0 opacity-40" />
            <div className="relative grid gap-8 md:grid-cols-2">
              <div>
                <h2 className="font-display text-3xl font-bold leading-tight md:text-4xl">{t("home.cta_title", "A quote, in the time it takes to skim a pool.")}</h2>
                <p className="mt-3 max-w-md text-cyan-50/90">
                  {t("home.cta_body", "Snap a few photos, tell us about the project, and we'll get back the same day. No sales calls, no surprises.")}
                </p>
                <Link to="/quote" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-medium text-cyan-800 shadow-lg transition hover:scale-[1.02]" data-testid="cta-get-quote-button">
                  {t("home.cta_button", "Start your quote")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="relative">
                <div className="rounded-2xl bg-white/10 p-6 backdrop-blur-md">
                  <QuoteIcon className="h-6 w-6 text-cyan-200" />
                  <p className="mt-3 font-display text-lg leading-relaxed text-white">
                    "{t("home.testimonial_quote", "They turned a tired backyard into the calmest place I own. Worth every penny.")}"
                  </p>
                  <div className="mt-4 text-sm text-cyan-200">{t("home.testimonial_author", "— L. Mendoza, Atlanta, GA")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP TEASER */}
      <section className="section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:grid-cols-2 md:px-8">
          <div className="overflow-hidden rounded-3xl">
            <img src="https://images.pexels.com/photos/30463419/pexels-photo-30463419.jpeg?auto=compress&cs=tinysrgb&w=900" alt="Pool" className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col justify-center">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">{t("home.shop_eyebrow", "The Shop")}</span>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 md:text-4xl">{t("home.shop_title", "Everything your pool needs, curated.")}</h2>
            <p className="mt-3 text-slate-600">{t("home.shop_body", "Chemicals, robotic cleaners, lighting, tools — and our seasonal service packages, all in one place.")}</p>
            <div className="mt-6">
              <Link to="/shop" className="btn-aqua" data-testid="home-visit-shop-button">
                {t("home.shop_cta", "Visit the shop")} <ShoppingBag className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
